import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Radio, Home, Search, MessageCircle, User, Settings, PlusCircle, Calendar } from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

const Navbar = ({ NotificationsPanel, onCreatePost }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [theme, setTheme] = useState('dark');
  
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Unread count error:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showProfileMenu && !e.target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
      if (showNotifications && !e.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu, showNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowProfileMenu(false);
  };

  const goToProfile = () => {
    if (!user) return;
    const userId = user.id || user._id;
    if (userId) {
      navigate(`/creator/${userId}`);
    } else {
      toast.error('ID do usuario nao encontrado');
    }
    setShowProfileMenu(false);
  };

  const handleBecomeCreator = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/me`,
        { role: 'creator' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bem-vindo como criador! Complete seu perfil.');
      navigate('/creator-setup');
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
    }
  };

  const getImageUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith('http')) return picture;
    if (picture.startsWith('/uploads')) return `${BACKEND_URL}/api${picture}`;
    if (picture.startsWith('data:')) return picture;
    return `data:image/jpeg;base64,${picture}`;
  };

  // Verificar se a rota atual está ativa
  const isActive = (path) => {
    if (path === '/feed') return location.pathname === '/feed' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Obter ID do usuário para navegação ao perfil
  const getUserProfilePath = () => {
    if (!user) return '/feed';
    const userId = user.id || user._id;
    if (userId) return `/creator/${userId}`;
    return '/feed';
  };

  return (
    <>
      {/* ========== TOP NAVBAR (Desktop & Mobile) ========== */}
      <nav className="bg-[#0D0D0D] border-b border-[#C79F5E]/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          {/* Logo */}
          <div
            onClick={() => navigate('/feed')}
            className="cursor-pointer flex items-center hover:opacity-80 transition-opacity"
          >
            <img src="/lyber-logo.png" alt="LYBERHOT" className="h-10 md:h-12 w-auto" />
          </div>

          <div className="flex items-center gap-3 md:gap-4">
            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-2">
              <button
                data-testid="nav-feed-btn"
                onClick={() => navigate('/feed')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive('/feed') 
                    ? 'bg-[#C79F5E]/20 text-[#C79F5E] border border-[#C79F5E]/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Feed
                </span>
              </button>
              
              <button
                data-testid="nav-lives-btn"
                onClick={() => navigate('/lives')}
                className={`px-4 py-2 rounded-lg font-medium transition-all relative ${
                  isActive('/lives') 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                    : 'text-gray-400 hover:text-red-400 hover:bg-red-500/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Radio className="w-4 h-4" />
                  Lives
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                </span>
              </button>
              
              <button
                data-testid="nav-creators-btn"
                onClick={() => navigate('/creators')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive('/creators') 
                    ? 'bg-[#C79F5E]/20 text-[#C79F5E] border border-[#C79F5E]/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Explorar
                </span>
              </button>
              
              <button
                data-testid="nav-messages-btn"
                onClick={() => navigate('/messages')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive('/messages') 
                    ? 'bg-[#C79F5E]/20 text-[#C79F5E] border border-[#C79F5E]/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Chat
                </span>
              </button>

              <button
                data-testid="nav-events-btn"
                onClick={() => navigate('/eventos')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive('/eventos') 
                    ? 'bg-[#C79F5E]/20 text-[#C79F5E] border border-[#C79F5E]/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Eventos
                </span>
              </button>
              
              {(user?.role === 'creator' || user?.role === 'free' || user?.role === 'fan') && (
                <button
                  data-testid="nav-dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/dashboard') 
                      ? 'bg-[#C79F5E]/20 text-[#C79F5E] border border-[#C79F5E]/30' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Dashboard
                  </span>
                </button>
              )}
              
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="bg-gradient-to-r from-[#4D1A4A] to-[#C79F5E] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:scale-105 transition-all hover:shadow-[0_0_20px_rgba(199,159,94,0.3)]"
                >
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Admin
                  </span>
                </button>
              )}
            </div>

            {/* Theme Toggle - Desktop only */}
            <button
              onClick={toggleTheme}
              className="hidden md:flex bg-[#1A1A1A] border border-[#C79F5E]/30 p-2 rounded-full hover:border-[#C79F5E]/60 transition-all"
              title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            >
              {theme === 'dark' ? (
                <svg className="w-6 h-6 text-[#C79F5E]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"/>
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
                </svg>
              )}
            </button>

            {/* Notifications */}
            <div className="relative notifications-container">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) loadUnreadCount();
                }}
                className="relative text-gray-400 hover:text-white transition-colors p-2"
                data-testid="notifications-btn"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {NotificationsPanel && (
                <NotificationsPanel 
                  show={showNotifications} 
                  onClose={() => setShowNotifications(false)}
                />
              )}
            </div>

            {/* Profile Menu - Desktop */}
            <div className="relative profile-menu-container hidden md:block">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C] border-2 border-[#C79F5E]/30">
                  {user?.picture ? (
                    <img
                      src={getImageUrl(user.picture)}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                      {user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <span className="text-white font-semibold hidden md:block">{user?.name}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showProfileMenu && (
                <div className="absolute right-0 top-12 bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-xl shadow-xl py-2 min-w-[200px] z-50">
                  <div className="px-4 py-3 border-b border-[#C79F5E]/10">
                    <p className="font-semibold text-white">{user?.name}</p>
                    <p className="text-sm text-gray-400">{user?.email}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-[#C79F5E]/20 text-[#C79F5E] text-xs rounded-full">
                      {user?.role === 'creator' ? 'Criador' : user?.role === 'admin' ? 'Admin' : user?.role === 'free' ? 'Free' : 'Fã'}
                    </span>
                  </div>
                  <button
                    onClick={goToProfile}
                    className="w-full text-left px-4 py-3 hover:bg-[#6B2D5C]/10 text-white transition-colors flex items-center gap-3"
                  >
                    <span>👤</span> Ver Perfil
                  </button>
                  {user?.account_kind === 'free' && (
                    <button
                      data-testid="my-invites-menu-btn"
                      onClick={() => navigate('/meus-convites')}
                      className="w-full text-left px-4 py-3 hover:bg-[#6B2D5C]/10 text-white transition-colors flex items-center gap-3"
                    >
                      <span>💌</span> Meus Convites
                    </button>
                  )}
                  <button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 hover:bg-[#6B2D5C]/10 text-[#C79F5E] transition-colors flex items-center gap-3"
                  >
                    <span>🚪</span> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ========== MOBILE BOTTOM NAVIGATION BAR ========== */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-lg border-t border-[#C79F5E]/20 pb-safe">
        <div className="flex items-center justify-around py-2 px-2">
          {/* Home / Feed */}
          <button
            onClick={() => navigate('/feed')}
            className={`flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all ${
              isActive('/feed') 
                ? 'text-[#C79F5E] bg-[#C79F5E]/10' 
                : 'text-gray-400 active:bg-white/5'
            }`}
            data-testid="mobile-nav-feed"
          >
            <Home className={`w-6 h-6 ${isActive('/feed') ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] mt-1 font-medium">Início</span>
          </button>

          {/* Search / Creators */}
          <button
            onClick={() => navigate('/creators')}
            className={`flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all ${
              isActive('/creators') 
                ? 'text-[#C79F5E] bg-[#C79F5E]/10' 
                : 'text-gray-400 active:bg-white/5'
            }`}
            data-testid="mobile-nav-creators"
          >
            <Search className={`w-6 h-6 ${isActive('/creators') ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] mt-1 font-medium">Buscar</span>
          </button>

          {/* Lives - Center Highlight Button */}
          <button
            onClick={() => navigate('/lives')}
            className={`flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all ${
              isActive('/lives') 
                ? 'text-red-500 bg-red-500/10' 
                : 'text-gray-400 active:bg-white/5'
            }`}
            data-testid="mobile-nav-lives"
          >
            <div className="relative">
              <Radio className={`w-6 h-6 ${isActive('/lives') ? 'stroke-[2.5px]' : ''}`} />
              {/* Pulsing dot for live indicator */}
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </div>
            <span className="text-[10px] mt-1 font-medium">Lives</span>
          </button>

          {/* Messages */}
          <button
            onClick={() => navigate('/messages')}
            className={`flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all ${
              isActive('/messages') 
                ? 'text-[#C79F5E] bg-[#C79F5E]/10' 
                : 'text-gray-400 active:bg-white/5'
            }`}
            data-testid="mobile-nav-messages"
          >
            <MessageCircle className={`w-6 h-6 ${isActive('/messages') ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] mt-1 font-medium">Chat</span>
          </button>

          {/* Profile */}
          <button
            onClick={() => navigate(getUserProfilePath())}
            className={`flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all ${
              isActive('/creator/') || isActive('/dashboard') 
                ? 'text-[#C79F5E] bg-[#C79F5E]/10' 
                : 'text-gray-400 active:bg-white/5'
            }`}
            data-testid="mobile-nav-profile"
          >
            {user?.picture ? (
              <div className={`w-6 h-6 rounded-full overflow-hidden border-2 ${
                isActive('/creator/') || isActive('/dashboard') 
                  ? 'border-[#C79F5E]' 
                  : 'border-gray-600'
              }`}>
                <img
                  src={getImageUrl(user.picture)}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <User className={`w-6 h-6 ${isActive('/creator/') || isActive('/dashboard') ? 'stroke-[2.5px]' : ''}`} />
            )}
            <span className="text-[10px] mt-1 font-medium">Perfil</span>
          </button>
        </div>

        {/* Admin Quick Access Button - Floating */}
        {user?.role === 'admin' && (
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="absolute -top-14 right-4 bg-gradient-to-r from-[#4D1A4A] to-[#C79F5E] text-white p-3 rounded-full shadow-lg shadow-[#C79F5E]/30 hover:scale-110 active:scale-95 transition-transform"
            data-testid="mobile-admin-floating-btn"
            title="Sala de Controle"
          >
            <Settings className="w-5 h-5" />
          </button>
        )}

        {/* Creator Quick Post Button - Floating */}
        {(user?.role === 'creator' || user?.role === 'free' || user?.role === 'fan') && !isActive('/admin') && (
          <button
            onClick={() => {
              if (onCreatePost) {
                onCreatePost();
              } else {
                // Fallback: navegar para o perfil
                navigate(getUserProfilePath());
              }
            }}
            className="absolute -top-14 right-4 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black p-3 rounded-full shadow-lg shadow-[#C79F5E]/30 hover:scale-110 active:scale-95 transition-transform"
            data-testid="mobile-create-post-btn"
            title="Criar Post"
          >
            <PlusCircle className="w-5 h-5" />
          </button>
        )}
      </nav>

      {/* Spacer for bottom navigation on mobile */}
      <div className="md:hidden h-20"></div>
    </>
  );
};

export default Navbar;
